import { format, parseISO, isValid } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { prependAppliedFilters } from "src/helpers/csvFilters";

export type ReportExportResponse = {
  data: string;
  filename: string;
  format: string;
  encoding: "gzip+base64" | string;
};

function getMimeType(format: string): string {
  switch (format.toLowerCase()) {
    case "xlsx":
      return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    case "csv":
      return "text/csv";
    default:
      return "application/octet-stream";
  }
}

export async function downloadExportFile(
  resp: ReportExportResponse,
  // When supplied, an "Applied Filters" block is prepended to the file so the
  // download records what produced it. CSV only — xlsx is a binary zip and
  // splicing text into it would corrupt the workbook.
  options?: { appliedFilters?: Record<string, unknown> },
): Promise<void> {
  if (resp.encoding !== "gzip+base64") {
    throw new Error(`Unsupported encoding: ${resp.encoding}`);
  }

  const base64Binary = atob(resp.data);
  const compressed = new Uint8Array(base64Binary.length);

  for (let i = 0; i < base64Binary.length; i += 1) {
    compressed[i] = base64Binary.charCodeAt(i);
  }

  const decompressionStream = new DecompressionStream("gzip");
  const decompressedStream = new Blob([compressed])
    .stream()
    .pipeThrough(decompressionStream);
  const fileBuffer = await new Response(decompressedStream).arrayBuffer();

  const isCsv = (resp.format || "").toLowerCase() === "csv";
  const blob =
    isCsv && options?.appliedFilters
      ? new Blob(
          [
            prependAppliedFilters(
              new TextDecoder("utf-8").decode(fileBuffer),
              options.appliedFilters,
            ),
          ],
          { type: getMimeType(resp.format) },
        )
      : new Blob([fileBuffer], { type: getMimeType(resp.format) });
  const url = URL.createObjectURL(blob);

  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = resp.filename || `report.${resp.format}`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function isExactFormatWith3Millis(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value);
}

function getTimeZoneFromValue(value: string): string | null {
  if (value.endsWith("Z") || value.endsWith("z")) return "UTC";

  const tzMatch = value.match(/([+-]\d{2}):?(\d{2})$/);
  if (!tzMatch) return null;

  return `${tzMatch[1]}:${tzMatch[2]}`;
}

export const formatDateMDY = (value: string | null): string => {
  if (!value) return "";

  let date: Date | null = null;

  if (isExactFormatWith3Millis(value)) {
    date = parseISO(value);
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    // date only
    date = parseISO(value);
  } else {
    date = parseISO(value);
  }

  if (!date || !isValid(date)) return "";

  const timeZone = getTimeZoneFromValue(value);

  const hasTime =
    value.includes("T") || // ISO datetime
    /\d{2}:\d{2}/.test(value); // 15:33 format

  // If time exists → show date + time
  if (hasTime) {
    return timeZone
      ? formatInTimeZone(date, timeZone, "M/d/yyyy h:mm a")
      : format(date, "M/d/yyyy h:mm a");
  }

  // Else → date only
  return timeZone
    ? formatInTimeZone(date, timeZone, "M/d/yyyy")
    : format(date, "M/d/yyyy");
};

export const formatValue = (value: any) => {
  if (!value) return "N/A";

  // If value is "zilretta" (case-insensitive)
  if (typeof value === "string" && value.trim().toLowerCase() === "zilretta") {
    return "ZILRETTA";
  }

  if (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(value)
  ) {
    const date = formatDateMDY(value);
    if (date.length > 0) return date;
  }

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    if (!isValid(parseISO(value))) return value.toString();

    const date = formatDateMDY(value);
    if (date.length > 0) return date;
  }

  return value.toString();
};
